import { Injectable } from '@angular/core';
import { Definitions } from '../../core/definitions';
import { UserData } from '../auth/auth.service';

@Injectable()
export class StorageService {
  constructor() {}

  encode(value: any) {
    if (Object.prototype.toString.call(value) === '[object Date]') {
      return '__ag_date|' + value.toUTCString();
    }
    if (typeof value === 'number') {
      return '__ag_numb|' + value;
    }
    if (typeof value === 'boolean') {
      return '__ag_bool|' + value ? '1' : '0';
    }
    if (typeof value === 'string') {
      return '__ag_strn|' + value;
    }
    if (value === Object(value)) {
      return '__ag_objt|' + JSON.stringify(value);
    }
    // Not encoded
    return value;
  }

  decode(value): any {
    const length = value.length;
    if (length < 10) {
      // Too short to be encoded
      return value;
    }
    const type = value.substr(0, 9);
    const data = value.substring(10);

    switch (type) {
      case '__ag_date':
        return new Date(data);
      case '__ag_numb':
        return Number(data);
      case '__ag_bool':
        return Boolean(data);
      case '__ag_strn':
        return data;
      case '__ag_objt':
        return JSON.parse(data);
      default:
        // No encoding found
        return value;
    }
  }

  // Local Storage
  getLocalItem(key: string): any {
    return this.localItemExists(key) ? this.decode(localStorage.getItem(key)) : null;
  }

  setLocalItem(key: string, data: any) {
    localStorage.setItem(key, this.encode(data));
  }

  removeLocalItem(key: string) {
    if (this.localItemExists(key)) {
      localStorage.removeItem(key);
    }
  }

  clearLocalStorage() {
    localStorage.clear();
  }

  localItemExists(key: string) {
    return localStorage.getItem(key) != null;
  }

  // Session Storage
  getSessionItem(key: string): any {
    return this.sessionItemExists(key) ? this.decode(sessionStorage.getItem(key)) : null;
  }

  setSessionItem(key: string, data: any) {
    sessionStorage.setItem(key, this.encode(data));
  }

  removeSessionItem(key: string) {
    if (this.sessionItemExists(key)) {
      sessionStorage.removeItem(key);
    }
  }

  clearSessionStorage() {
    sessionStorage.clear();
  }

  sessionItemExists(key: string) {
    return sessionStorage.getItem(key) != null;
  }

  // Legacy Session Storage
  getItem(key) {
    const raw = sessionStorage.getItem(key);
    if (this.has(key) && raw && raw !== 'undefined') {
      return JSON.parse(raw);
    }
    return undefined;
  }

  setItem(key, data) {
    if (data === undefined) {
      sessionStorage.removeItem(key);
      return data;
    }
    sessionStorage.setItem(key, JSON.stringify(data));
    return data;
  }

  remove(key) {
    let result = false;
    if (this.has(key)) {
      sessionStorage.removeItem(key);
      result = true;
    }
    return result;
  }

  clearAll() {
    const cid = this.getCid();
    const lid = this.getLid();

    sessionStorage.clear();

    this.setCid(cid);
    this.setLid(lid);
  }

  has(key: string): boolean {
    const item = sessionStorage.getItem(key);
    return item !== null && item !== undefined && item !== '';
  }

  getIsKidsCat(): boolean {
    return this.getItem('kidsCat');
  }

  setIsKidsCat(isKidsCat): boolean {
    return this.setItem('kidsCat', isKidsCat);
  }

  setCid(data) {
    this.setItem('cid', data);
  }

  getCid() {
    return this.getItem('cid');
  }

  setLid(data) {
    this.setItem('lid', data);
  }

  getLid() {
    return this.getItem('lid');
  }

  setUserData(userData): void {
    this.setItem(Definitions.userDataSessionKey, userData);
  }

  getUserData(): UserData {
    return this.getItem(Definitions.userDataSessionKey);
  }

  setCurrentPatronValue(value): void {
    this.setItem('_curr_pat', value);
  }

  getCurrentPatronValue(): string {
    let result = '';
    if (this.has('_curr_pat')) {
      result = this.getItem('_curr_pat');
      // result = JSON.parse(result);
    }
    return result;
  }

  setPrevPatronValue(value): void {
    this.setItem('_prev_pat', value);
  }

  getPrevPatronValue(): string {
    let result = '';
    if (this.has('_prev_pat')) {
      result = this.getItem('_prev_pat');
      // result = JSON.parse(result);
    }
    return result;
  }

  // TODO: getCurrentPatronValue and getPrevPatronValue references the same user, they're not actually different, but technically are. CurrentPatron is 'fullname:userId' and PreviousPatron is 'username', (i.e. barcode)
  // Should check how both are referenced and how the values are actually used to determine consolidation fix
  isCurrentPatron(): boolean {
    return this.getCurrentPatronValue() !== '' && this.getPrevPatronValue() !== '';
  }

  setPastLid(value): void {
    this.setItem('_past_lid', value);
  }
  getPastLid(): string {
    return this.getItem('_past_lid');
  }
  removePastLid(): void {
    if (this.has('_past_lid')) {
      this.remove('_past_lid');
    }
  }

  setFetchExpressLibrary(flag: string): void {
    this.setItem('_fet_exp_lib', flag);
  }

  getFetchExpressLibrary(): string {
    return this.getItem('_fet_exp_lib');
  }

  setFetchExpressHeader(flag: string): void {
    this.setItem('_fet_exp_head', flag);
  }

  getFetchExpressHeader(): string {
    return this.getItem('_fet_exp_head');
  }

  setFetchExpressFooter(flag) {
    this.setItem('_fet_exp_foot', flag);
  }

  getFetchExpressFooter() {
    return this.getItem('_fet_exp_foot');
  }

  getShowLibBox(): boolean {
    return this.getItem('_islibbox');
  }

  setShowLibBox(record: boolean) {
    this.setItem('_islibbox', record);
  }

  setAgControlIdFromSearchResults(agControlId: string) {
    this.setItem(Definitions.SEARCHRESULTS_FULLRECORD_AGCONTROLID, agControlId);
  }

  /**
   * Search Result record tracking for scroll
   * Gets and removes the agControlId from the session storage
   * @returns
   */
  takeAgControlIdFromSearchResults() {
    const agControlId = this.getItem(Definitions.SEARCHRESULTS_FULLRECORD_AGCONTROLID);
    this.remove(Definitions.SEARCHRESULTS_FULLRECORD_AGCONTROLID);
    return agControlId;
  }

  setSearchResultsYOffset(yOffset: number) {
    this.setItem(Definitions.SEARCHRESULTS_YOFFSET, yOffset);
  }

  /**
   * Search Result record tracking for scroll
   * Gets and removes the yOffset from the session storage
   * @returns
   */
  takeSearchResultsYOffset() {
    const yOffset = this.getItem(Definitions.SEARCHRESULTS_YOFFSET);
    this.remove(Definitions.SEARCHRESULTS_YOFFSET);
    return yOffset;
  }

  getBriefRecordCache() {
    return this.getItem('brief');
  }

  setBriefRecordCache(record) {
    this.setItem('brief', record);
  }

  setBackFrmKC(url) {
    this.setItem('backFrmKC', url);
  }
  getBackFrmKC() {
    return this.getItem('backFrmKC');
  }
  setBackFrmBrief(url) {
    this.setItem('backFrmBrief', url);
  }
  getBackFrmBrief() {
    return this.getItem('backFrmBrief');
  }
  setHomeUrl(url) {
    this.setItem('homeUrl', url);
  }
  getHomeUrl() {
    return this.getItem('homeUrl');
  }

  setIsNewPage(value) {
    this.setItem('_isNP', value);
  }
  getIsNewPage() {
    return this.getItem('_isNP');
  }

  setSearchView(value: string) {
    this.setItem(Definitions.searchViewSessionKey, value);
  }

  getSearchView() {
    return this.getItem(Definitions.searchViewSessionKey);
  }

  setSearchTerm(data) {
    this.setItem(Definitions.searchTermSessionKey, data);
  }

  setRequestItemsForBatchIll(data) {
    this.setItem(Definitions.requestItemsBatchSessionKey, data);
  }

  getRequestItemsForBatchIll() {
    return this.getItem(Definitions.requestItemsBatchSessionKey);
  }

  setFullRecordsForBatchIll(data) {
    this.setItem(Definitions.fullRecordItemsBatchSessionKey, data);
  }

  getFullRecordsForBatchIll() {
    return this.getItem(Definitions.fullRecordItemsBatchSessionKey);
  }

  getSearchTerm() {
    return this.getItem(Definitions.searchTermSessionKey);
  }

  setDidYouMeanSearchTerm(data: string) {
    this.setItem(Definitions.didYouMeanSearchTermSessionKey, data);
  }

  removeDidYouMeanSearchTerm() {
    this.remove(Definitions.didYouMeanSearchTermSessionKey);
  }

  getDidYouMeanSearchTerm() {
    return this.getItem(Definitions.didYouMeanSearchTermSessionKey);
  }
}
